import { Model, Factory, trait } from 'miragejs'
import type { ModelDefinition } from 'miragejs/-types'
import {
  randPhrase,
  randCompanyName,
  randAnimal,
  rand,
  randAlphaNumeric,
} from '@ngneat/falso'

import type { SchemaAttributes } from '../types.js'
import type { MirageServer, MockServer } from './server.js'
import {
  ListItemsTypes,
  type ListItem,
  type ListItemsQueryParams,
} from '@/composables/listItems.js'
import {
  AvailablePatientSpecies,
  type PatientSpecies,
} from '@/composables/usePatientDetails.js'

export type ListItemsData = SchemaAttributes<'ListItem'>

export const model = Model as ModelDefinition<ListItemsData>

export const generateBreed: Record<PatientSpecies, () => string> = {
  Cat: () => randAnimal(),
  Dog: () => randAnimal(),
  Bird: () => randAnimal(),
  Rabbit: () => randAnimal(),
  Horse: () => randAnimal(),
}

function generateCode(type: ListItem, pattern: string = '#####'): string {
  return `${type}-${randAlphaNumeric({ length: pattern.length }).join('')}`
}

export const factory = Factory.extend<ListItemsData>({
  code() {
    return null
  },
  label() {
    return randPhrase({ maxCharCount: 60 })
  },
  asInsuranceCompany: trait({
    afterCreate(listItem) {
      listItem.update({
        code: generateCode(ListItemsTypes.Insurance, '************'),
        label: randCompanyName(),
      })
    },
  }),
  asReportingDimension1: trait({
    afterCreate(listItem) {
      listItem.update({
        code: generateCode(ListItemsTypes.ReportingDimension1),
      })
    },
  }),
  asConsultationNoteType: trait({
    afterCreate(listItem) {
      listItem.update({
        code: generateCode(ListItemsTypes.ConsultationNoteType),
      })
    },
  }),
  asReportingDimension2: trait({
    afterCreate(listItem) {
      listItem.update({
        code: generateCode(ListItemsTypes.ReportingDimension2),
      })
    },
  }),
  asSpecies: trait({
    afterCreate(listItem) {
      listItem.update({
        code: generateCode(ListItemsTypes.Species),
        label: rand(AvailablePatientSpecies),
      })
    },
  }),
  asBreed: trait({
    afterCreate(listItem) {
      const species = rand(AvailablePatientSpecies)

      listItem.update({
        code: `${species}-${ListItemsTypes.Breed}-${randAlphaNumeric({ length: 5 }).join('')}`,
        label: generateBreed[species](),
      })
    },
  }),
})

export function routes(server: MockServer) {
  server.get('/list_items', function (this: MirageServer, schema, request) {
    const queryParams = request.queryParams as unknown as ListItemsQueryParams
    const listItems = schema
      .all('listItem')
      .filter((item) =>
        item.attrs.code?.includes(queryParams['filter[list_type]']),
      )
    return listItems
  })
}

export function seeds(server: MockServer) {
  server.createList('listItem', 10, 'asInsuranceCompany')
  server.createList('listItem', 20, 'asReportingDimension1')
  server.createList('listItem', 20, 'asReportingDimension2')

  AvailablePatientSpecies.forEach((species) => {
    server.create('listItem', 'asSpecies', { label: species })
  })

  server.createList('listItem', 20, 'asBreed')
}
